import type { Injector } from '@angular/core';
import { inject, runInInjectionContext } from '@angular/core';
import {
    assertInjector,
    isResolvedProblemDetails,
    qpxToast as toast,
} from '@quipex/core/utils';
import {
    BuildingOwnershipTransferDecision,
    OrganisationsApiService,
    type BuildingOwnershipTransferResponse,
} from '@quipex/shared/contracts';
import { DialogService, isDialogConfirmedOnly } from '@quipex/shared/ui';
import { firstValueFrom, take, tap } from 'rxjs';
import { decideBuildingOwnershipTransfer } from './api';

export interface DecideBuildingOwnershipTransferDialogRequest {
    organisationId: string;
    transfer: BuildingOwnershipTransferResponse;
    decision: BuildingOwnershipTransferDecision;
    onDecided?: () => void;
}

export interface DecideBuildingOwnershipTransferDialogLauncher {
    open: (request: DecideBuildingOwnershipTransferDialogRequest) => void;
}

export function injectDecideBuildingOwnershipTransferDialog(
    injector?: Injector
): DecideBuildingOwnershipTransferDialogLauncher {
    const resolvedInjector = assertInjector(
        injectDecideBuildingOwnershipTransferDialog,
        injector
    );

    const open = (request: DecideBuildingOwnershipTransferDialogRequest) => {
        runInInjectionContext(resolvedInjector, () => {
            const dialogService = inject(DialogService);
            const organisationsApi = inject(OrganisationsApiService);
            const copy = toDecisionCopy(request);

            const dialogRef = dialogService.openConfirmationDialog({
                status:
                    request.decision ===
                    BuildingOwnershipTransferDecision.Accept
                        ? 'default'
                        : 'caution',
                title: copy.title,
                text1: copy.text,
                confirmButton: copy.confirmButtonText,
                cancelButton: 'Cancel',
            });

            dialogRef
                .afterClosed()
                .pipe(isDialogConfirmedOnly(), take(1))
                .subscribe(() =>
                    handleConfirmedTransfer(organisationsApi, request, copy)
                );
        });
    };

    return { open };
}

function handleConfirmedTransfer(
    organisationsApi: OrganisationsApiService,
    request: DecideBuildingOwnershipTransferDialogRequest,
    copy: ReturnType<typeof toDecisionCopy>
): void {
    const decisionPromise = firstValueFrom(
        decideBuildingOwnershipTransfer(
            organisationsApi,
            request.organisationId,
            request.transfer.id,
            request.decision
        ).pipe(tap(() => request.onDecided?.()))
    );

    toast.promise(decisionPromise, {
        loading: copy.loadingText,
        success: copy.successText,
        error: (error: unknown) => toDecisionErrorMessage(error, request),
    });
}

function toDecisionCopy(
    request: DecideBuildingOwnershipTransferDialogRequest
): {
    title: string;
    text: string;
    confirmButtonText: string;
    loadingText: string;
    successText: string;
} {
    const buildingName = request.transfer.buildingName;
    const ownerName = request.transfer.currentOwnerOrganisationName;

    if (request.decision === BuildingOwnershipTransferDecision.Accept) {
        const retainedAccessCopy = request.transfer.retainOriginalOwnerAccess
            ? ` ${ownerName} will retain Building Agent access. Your organisation can revoke this access.`
            : '';

        return {
            title: 'Accept ownership transfer',
            text: `This will transfer ownership of ${buildingName} from ${ownerName} to your organisation.${retainedAccessCopy}`,
            confirmButtonText: 'Accept transfer',
            loadingText: 'Accepting transfer...',
            successText: 'Building ownership transferred',
        };
    }

    return {
        title: 'Reject ownership transfer',
        text: `This will reject the transfer request for ${buildingName}. Ownership will remain with ${ownerName}.`,
        confirmButtonText: 'Reject transfer',
        loadingText: 'Rejecting transfer...',
        successText: 'Transfer rejected',
    };
}

function toDecisionErrorMessage(
    error: unknown,
    request: DecideBuildingOwnershipTransferDialogRequest
): string {
    if (isResolvedProblemDetails(error)) {
        return error.detail ?? error.title;
    }

    if (request.decision === BuildingOwnershipTransferDecision.Reject) {
        return 'Failed to reject transfer';
    }

    return request.decision === BuildingOwnershipTransferDecision.Accept
        ? 'Failed to accept transfer'
        : 'Failed to reject transfer';
}
